/**
 * Calls `refresh` when the page regains attention and on an interval while
 * it is visible.
 *
 * Attention is the window `focus` event or `visibilitychange` to visible;
 * switching back to the tab usually fires both, so the caller's refresh must
 * tolerate back-to-back calls (`usePortalFeed` drops a call while one is in
 * flight). The interval tick skips while the document is hidden, so a
 * background tab makes no requests. The latest `refresh` is read through a
 * ref, so a new callback identity never restarts the timer.
 */

import { useEffect, useRef } from "react";

export function useLiveRefresh(refresh: () => void, intervalMs: number): void {
  const latest = useRef(refresh);
  useEffect(() => {
    latest.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const visible = (): boolean => document.visibilityState === "visible";
    const onFocus = (): void => latest.current();
    const onVisibility = (): void => {
      if (visible()) latest.current();
    };
    const timer = window.setInterval(() => {
      if (visible()) latest.current();
    }, intervalMs);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
